import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { 
  Card, 
  Button, 
  Space, 
  Tag, 
  message,
  Spin,
  Image,
  Typography,
  Row,
  Col,
  Statistic
} from 'antd';
import {
  PlayCircleOutlined,
  ShareAltOutlined,
  CopyOutlined,
  GlobalOutlined,
  UserOutlined,
  ClockCircleOutlined,
  ThunderboltOutlined,
  RocketOutlined,
  ReloadOutlined,
  DatabaseOutlined,
  CloudServerOutlined
} from '@ant-design/icons';
// 分享页面使用公开API，不需要认证

const { Title, Paragraph, Text } = Typography;

interface Server {
  id: number;
  name: string;
  game_name: string;
  game_icon?: string;
  background_image_url?: string;
  status: string;
  port: number;
  max_players: number;
  expires_at: string;
  created_at: string;
  node_name: string;
  node_host: string;
  supports_mods: boolean;
  is_steam_game: boolean;
  mod_support_enabled: number;
  config: any;
}

const ServerShare: React.FC = () => {
  const { serverId } = useParams<{ serverId: string }>();
  const [server, setServer] = useState<Server | null>(null);
  const [loading, setLoading] = useState(true);
  const [joinLoading, setJoinLoading] = useState(false);
  const [shareLoading, setShareLoading] = useState(false);
  const [onlineCount, setOnlineCount] = useState<number | null>(null);
  const [onlineCountLoading, setOnlineCountLoading] = useState(false);
  const [gameMode, setGameMode] = useState<string | null>(null);
  const [mapName, setMapName] = useState<string | null>(null);
  const [serverInfoLoading, setServerInfoLoading] = useState(false);
  const [siteConfig, setSiteConfig] = useState<any>(null);
  const [gameTypeBackground, setGameTypeBackground] = useState<string | null>(null);
  const [currentBackground, setCurrentBackground] = useState<string>('default');
  const [backgroundLoading, setBackgroundLoading] = useState(false);

  const loadServerDetail = async () => {
    try {
      console.log('🔄 加载服务器详情:', serverId);
      // 使用公开API，不需要认证
      const response = await fetch(`/api/servers/public/${serverId}`);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      console.log('✅ 服务器详情数据:', data);
      setServer(data);
      
      // 设置游戏类型背景图片
      console.log('🎨 检查背景图片数据:', data.background_image_url);
      if (data.background_image_url) {
        console.log('✅ 设置游戏类型背景:', data.background_image_url);
        setGameTypeBackground(data.background_image_url);
        setCurrentBackground('game'); // 默认使用游戏类型背景
      } else {
        console.log('⚠️ 没有游戏类型背景图片，使用默认背景');
      }
    } catch (error: any) {
      console.error('❌ 加载服务器详情失败:', error);
      message.error(error.message || '服务器不存在或已被删除');
    } finally {
      setLoading(false);
    }
  };

  const getOnlineCount = async () => {
    if (!serverId || !server || server.status !== 'running') {
      setOnlineCount(null);
      return;
    }

    setOnlineCountLoading(true);
    try {
      console.log('🔄 查询在线人数:', serverId);
      // 使用公开API，不需要认证
      const response = await fetch(`/api/ark-rcon/public/servers/${serverId}/players`);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      console.log('✅ 在线人数数据:', data);
      
      if (data && Array.isArray(data)) {
        setOnlineCount(data.length);
      } else {
        setOnlineCount(0);
      }
    } catch (error: any) {
      console.error('❌ 获取在线人数失败:', error);
      setOnlineCount(null);
    } finally {
      setOnlineCountLoading(false);
    }
  };

  const getServerInfo = async () => {
    if (!serverId || !server || server.status !== 'running') {
      setGameMode(null);
      setMapName(null);
      return;
    }

    setServerInfoLoading(true);
    try {
      console.log('🔄 查询服务器信息:', serverId);
      
      // 从服务器配置中获取游戏模式和地图信息
      // ARK服务器的这些信息通常存储在配置中，而不是通过RCON获取
      if (server.config) {
        try {
          let config;
          if (typeof server.config === 'string') {
            // 检查是否是有效的JSON字符串
            if (server.config.trim().startsWith('{') || server.config.trim().startsWith('[')) {
              config = JSON.parse(server.config);
            } else {
              console.log('⚠️ 服务器配置不是有效的JSON格式，使用默认值');
              config = {};
            }
          } else {
            config = server.config;
          }
          
          // 从配置中提取游戏模式
          if (config.GameMode) {
            setGameMode(config.GameMode);
          } else if (config.ServerSettings && config.ServerSettings.GameMode) {
            setGameMode(config.ServerSettings.GameMode);
          } else {
            // 根据ARK服务器的默认设置
            setGameMode('PvP');
          }
          
          // 从配置中提取地图名称
          if (config.Map) {
            setMapName(config.Map);
          } else if (config.ServerSettings && config.ServerSettings.Map) {
            setMapName(config.ServerSettings.Map);
          } else {
            // 根据ARK服务器的默认设置
            setMapName('The Island');
          }
          
          console.log('✅ 从配置中获取服务器信息:', { 
            gameMode: config.GameMode || 'PvP', 
            mapName: config.Map || 'The Island' 
          });
        } catch (configError) {
          console.error('❌ 解析服务器配置失败:', configError);
          // 设置默认值
          setGameMode('PvP');
          setMapName('The Island');
        }
      } else {
        // 没有配置信息，设置默认值
        setGameMode('PvP');
        setMapName('The Island');
      }

    } catch (error: any) {
      console.error('❌ 获取服务器信息失败:', error);
      setGameMode(null);
      setMapName(null);
    } finally {
      setServerInfoLoading(false);
    }
  };

  const loadSiteConfig = async () => {
    try {
      console.log('🔄 加载网站配置...');
      const response = await fetch('/api/admin/system/public-config');
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      console.log('✅ 网站配置数据:', data);
      setSiteConfig(data);
    } catch (error: any) {
      console.error('❌ 加载网站配置失败:', error);
    }
  };

  useEffect(() => {
    if (serverId) {
      loadServerDetail();
      loadSiteConfig();
    }
  }, [serverId]);

  useEffect(() => {
    if (server && server.status === 'running') {
      // 立即获取一次在线人数和服务器信息
      getOnlineCount();
      getServerInfo();
      
      // 设置定时器，每30秒更新一次在线人数和服务器信息
      const interval = setInterval(() => {
        getOnlineCount();
        getServerInfo();
      }, 30000);

      return () => clearInterval(interval);
    } else {
      setOnlineCount(null);
      setGameMode(null);
      setMapName(null);
    }
  }, [server]);

  const getSteamConnectLink = () => {
    if (!server) return '';
    return `steam://connect/${server.node_host}:${server.port}`;
  };

  const getBackgroundImage = () => {
    console.log('🖼️ 获取背景图片 - currentBackground:', currentBackground, 'gameTypeBackground:', gameTypeBackground);
    if (currentBackground === 'game' && gameTypeBackground) {
      console.log('✅ 使用游戏类型背景:', gameTypeBackground);
      return gameTypeBackground;
    }
    console.log('🔄 使用默认背景');
    return 'https://i1.hdslb.com/bfs/archive/a87e111fca69e275ad78f71129647070a5d39ea9.jpg';
  };

  const handleJoinGame = async () => {
    setJoinLoading(true);
    try {
      const steamLink = getSteamConnectLink();
      if (steamLink) {
        // 尝试打开Steam链接
        window.open(steamLink, '_blank');
        message.success('正在启动Steam并连接到服务器...');
      }
    } catch (error) {
      message.error('无法连接到Steam，请确保Steam已安装并运行');
    } finally {
      setJoinLoading(false);
    }
  };

  const handleSharePage = async () => {
    setShareLoading(true);
    try {
      const shareUrl = window.location.href;
      await navigator.clipboard.writeText(shareUrl);
      message.success('分享链接已复制到剪贴板！');
    } catch (error) {
      message.error('复制失败，请手动复制链接');
    } finally {
      setShareLoading(false);
    }
  };

  const handleBackgroundSwitch = () => {
    setBackgroundLoading(true);
    setTimeout(() => {
      if (currentBackground === 'default') {
        setCurrentBackground('game');
        message.success('已切换到游戏类型背景');
      } else {
        setCurrentBackground('default');
        message.success('已切换到默认背景');
      }
      setBackgroundLoading(false);
    }, 300);
  };


  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
        overflow: 'hidden'
      }}>
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage: 'url(https://i1.hdslb.com/bfs/archive/a87e111fca69e275ad78f71129647070a5d39ea9.jpg)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          opacity: 0.3,
          filter: 'blur(2px)'
        }} />
        <div style={{
          position: 'relative',
          zIndex: 2,
          textAlign: 'center',
          color: 'white'
        }}>
          <Spin size="large" />
          <div style={{ marginTop: '20px', fontSize: '18px', fontWeight: 'bold' }}>
            正在加载服务器信息...
          </div>
        </div>
      </div>
    );
  }

  if (!server) {
    return (
      <div style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
        overflow: 'hidden'
      }}>
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage: 'url(https://i1.hdslb.com/bfs/archive/a87e111fca69e275ad78f71129647070a5d39ea9.jpg)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          opacity: 0.3,
          filter: 'blur(2px)'
        }} />
        <div style={{
          position: 'relative',
          zIndex: 2,
          textAlign: 'center',
          color: 'white'
        }}>
          <div style={{ fontSize: '24px', fontWeight: 'bold', marginBottom: '10px' }}>
            服务器不存在
          </div>
          <div style={{ fontSize: '16px', opacity: 0.8 }}>
            该服务器可能已被删除或不存在
          </div>
        </div>
      </div>
    );
  }

  const isExpired = new Date(server.expires_at) < new Date();

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      position: 'relative',
      overflow: 'hidden'
    }}>
      {/* 背景图片 */}
      <div style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundImage: `url(${getBackgroundImage()})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        opacity: 0.6,
        filter: 'blur(1px)',
        transition: 'all 0.5s ease-in-out'
      }} />

      {/* 动态粒子效果 */}
      <div style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 1
      }}>
        {[...Array(20)].map((_, i) => (
          <div
            key={i}
            style={{
              position: 'absolute',
              width: '4px',
              height: '4px',
              background: 'rgba(255, 255, 255, 0.6)',
              borderRadius: '50%',
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animation: `float ${3 + Math.random() * 4}s ease-in-out infinite`,
              animationDelay: `${Math.random() * 2}s`
            }}
          />
        ))}
      </div>

      {/* 主要内容 */}
      <div style={{
        position: 'relative',
        zIndex: 2,
        padding: '20px',
        maxWidth: '1200px',
        margin: '0 auto',
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center'
      }}>
        {/* 头部信息 */}
        <div style={{
          marginBottom: '40px',
          color: 'white',
          width: '98%'
        }}>
          <div style={{
            padding: '32px',
            background: 'rgba(255, 255, 255, 0.15)',
            backdropFilter: 'blur(25px)',
            borderRadius: '24px',
            border: '1px solid rgba(255, 255, 255, 0.25)',
            boxShadow: '0 12px 40px rgba(0, 0, 0, 0.4)',
            maxWidth: '100%',
            width: '100%'
          }}>
            <Row gutter={[32, 0]} align="middle">
              {/* 左侧：网站信息 */}
              <Col xs={24} md={8}>
                <div style={{ textAlign: 'center' }}>
                  {/* 网站Logo */}
                  {siteConfig?.logo_url && (
                    <div style={{ marginBottom: '16px' }}>
                      <Image
                        src={siteConfig.logo_url}
                        alt={siteConfig.site_name || '网站Logo'}
                        width={siteConfig.logo_width || 120}
                        height={siteConfig.logo_height || 40}
                        style={{ 
                          objectFit: 'contain',
                          borderRadius: '8px',
                          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)'
                        }}
                        preview={false}
                        fallback="data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTIwIiBoZWlnaHQ9IjQwIiB2aWV3Qm94PSIwIDAgMTIwIDQwIiBmaWxsPSJub25lIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciPgo8cmVjdCB3aWR0aD0iMTIwIiBoZWlnaHQ9IjQwIiBmaWxsPSIjRjVGNUY1Ii8+Cjx0ZXh0IHg9IjYwIiB5PSIyMCIgZm9udC1mYW1pbHk9IkFyaWFsIiBmb250LXNpemU9IjE0IiBmaWxsPSIjOTk5IiB0ZXh0LWFuY2hvcj0ibWlkZGxlIiBkeT0iLjNlbSI+TG9nbzwvdGV4dD4KPC9zdmc+"
                      />
                    </div>
                  )}
                  
                  <Title level={3} style={{ 
                    color: 'white', 
                    margin: '0 0 8px 0',
                    fontSize: 'clamp(18px, 4vw, 24px)',
                    fontWeight: '600',
                    textShadow: '0 2px 8px rgba(0, 0, 0, 0.5)'
                  }}>
                    {siteConfig?.site_name || '游戏面板管理系统'}
                  </Title>
                  <Paragraph style={{ 
                    color: 'rgba(255, 255, 255, 0.8)', 
                    margin: 0,
                    fontSize: 'clamp(12px, 2.5vw, 14px)',
                    fontWeight: '400'
                  }}>
                    {siteConfig?.site_description || '专业的游戏服务器托管平台'}
                  </Paragraph>
                </div>
              </Col>

              {/* 中间：分隔线 */}
              <Col xs={24} md={1} style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                <div style={{
                  width: '2px',
                  height: '80px',
                  background: 'linear-gradient(to bottom, transparent, rgba(255, 255, 255, 0.3), transparent)',
                  borderRadius: '1px'
                }} />
              </Col>

              {/* 右侧：服务器信息 */}
              <Col xs={24} md={15}>
                <div style={{ textAlign: 'center' }}>
                  <Image
                    src={server.game_icon || '/images/game-icons/placeholder_184x69.jpg'}
                    alt={server.game_name}
                    width={100}
                    height={37}
                    style={{ 
                      objectFit: 'cover', 
                      borderRadius: '8px',
                      marginBottom: '16px',
                      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)'
                    }}
                    preview={false}
                    fallback="data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAwIiBoZWlnaHQ9IjQwIiB2aWV3Qm94PSIwIDAgMTAwIDQwIiBmaWxsPSJub25lIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciPgo8cmVjdCB3aWR0aD0iMTAwIiBoZWlnaHQ9IjQwIiBmaWxsPSIjRjVGNUY1Ii8+Cjx0ZXh0IHg9IjUwIiB5PSIyMCIgZm9udC1mYW1pbHk9IkFyaWFsIiBmb250LXNpemU9IjE0IiBmaWxsPSIjOTk5IiB0ZXh0LWFuY2hvcj0ibWlkZGxlIiBkeT0iLjNlbSI+R2FtZTwvdGV4dD4KPC9zdmc+"
                  />
                  <Title level={2} style={{ 
                    color: 'white', 
                    margin: '0 0 8px 0', 
                    fontSize: 'clamp(18px, 4vw, 24px)',
                    fontWeight: '600',
                    textShadow: '0 2px 8px rgba(0, 0, 0, 0.5)'
                  }}>
                    {server.name}
                  </Title>
                  <Paragraph style={{ 
                    color: 'rgba(255, 255, 255, 0.9)', 
                    fontSize: 'clamp(14px, 2.5vw, 16px)', 
                    margin: '0 0 20px 0',
                    fontWeight: '400'
                  }}>
                    {server.game_name} 服务器
                  </Paragraph>
                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'center', 
                    alignItems: 'center', 
                    gap: '12px', 
                    flexWrap: 'wrap',
                    maxWidth: '100%'
                  }}>
                    <Tag 
                      color={server.status === 'running' ? 'green' : server.status === 'stopped' ? 'red' : 'orange'} 
                      style={{ fontSize: '14px', padding: '4px 12px', height: '28px', lineHeight: '20px' }}
                    >
                      {server.status === 'running' ? '运行中' : 
                       server.status === 'stopped' ? '已停止' : 
                       server.status === 'starting' ? '启动中' : 
                       server.status === 'restarting' ? '重启中' : 
                       server.status === 'installing' ? '安装中' : 
                       server.status === 'error' ? '错误' : server.status}
                    </Tag>
                    {server.is_steam_game && <Tag color="blue" style={{ fontSize: '14px', padding: '4px 12px', height: '28px', lineHeight: '20px' }}>Steam</Tag>}
                    {server.mod_support_enabled === 1 && <Tag color="green" style={{ fontSize: '14px', padding: '4px 12px', height: '28px', lineHeight: '20px' }}>MOD支持</Tag>}
                    {server.status === 'running' && onlineCount !== null && (
                      <Tag color={onlineCount > 0 ? 'green' : 'orange'} style={{ fontSize: '14px', padding: '4px 12px', height: '28px', lineHeight: '20px' }}>
                        {onlineCountLoading ? '查询中...' : `${onlineCount} 人在线`}
                      </Tag>
                    )}
                    {gameMode && (
                      <Tag color="purple" style={{ fontSize: '14px', padding: '4px 12px', height: '28px', lineHeight: '20px' }}>🎮 {gameMode}</Tag>
                    )}
                    {mapName && (
                      <Tag color="orange" style={{ fontSize: '14px', padding: '4px 12px', height: '28px', lineHeight: '20px' }}>🗺️ {mapName}</Tag>
                    )}
                  </div>
                </div>
              </Col>
            </Row>
          </div>
        </div>

        {/* 服务器信息卡片 */}
        <Row gutter={[20, 20]} style={{ marginBottom: '40px', width: '100%' }}>
          <Col xs={24} lg={12}>
            <Card
              style={{
                background: 'rgba(255, 255, 255, 0.98)',
                backdropFilter: 'blur(25px)',
                borderRadius: '20px',
                border: '1px solid rgba(255, 255, 255, 0.4)',
                boxShadow: '0 20px 60px rgba(0, 0, 0, 0.25)',
                height: '100%',
                transition: 'all 0.3s ease'
              }}
              bodyStyle={{ padding: '24px' }}
            >
              <div style={{ textAlign: 'center' }}>
                <GlobalOutlined style={{ fontSize: '52px', color: '#1890ff', marginBottom: '20px' }} />
                <Title level={3} style={{ margin: '0 0 20px 0', fontSize: '20px', fontWeight: '600' }}>服务器地址</Title>
                
                {/* 游戏端口 */}
                <div style={{ marginBottom: '16px' }}>
                  <Text type="secondary" style={{ fontSize: '15px', display: 'block', marginBottom: '10px', fontWeight: '500' }}>
                    🎮 游戏端口
                  </Text>
                  <div style={{
                    padding: '16px 24px',
                    background: 'linear-gradient(135deg, #f0f2f5 0%, #e6f7ff 100%)',
                    borderRadius: '12px',
                    fontSize: '20px',
                    fontWeight: 'bold',
                    color: '#1890ff',
                    fontFamily: 'monospace',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    border: '2px solid transparent',
                    position: 'relative',
                    overflow: 'hidden'
                  }}
                  onClick={() => {
                    navigator.clipboard.writeText(`${server.node_host}:${server.port}`);
                    message.success('游戏端口已复制到剪贴板');
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = 'linear-gradient(135deg, #e6f7ff 0%, #bae7ff 100%)';
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 8px 25px rgba(24, 144, 255, 0.3)';
                    e.currentTarget.style.borderColor = '#1890ff';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'linear-gradient(135deg, #f0f2f5 0%, #e6f7ff 100%)';
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = 'none';
                    e.currentTarget.style.borderColor = 'transparent';
                  }}
                  >
                    {server.node_host}:{server.port}
                  </div>
                </div>

                {/* 查询端口 */}
                <div>
                  <Text type="secondary" style={{ fontSize: '15px', display: 'block', marginBottom: '10px', fontWeight: '500' }}>
                    🔍 查询端口
                  </Text>
                  <div style={{
                    padding: '16px 24px',
                    background: 'linear-gradient(135deg, #f0f2f5 0%, #f6ffed 100%)',
                    borderRadius: '12px',
                    fontSize: '20px',
                    fontWeight: 'bold',
                    color: '#52c41a',
                    fontFamily: 'monospace',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    border: '2px solid transparent',
                    position: 'relative',
                    overflow: 'hidden'
                  }}
                  onClick={() => {
                    const queryPort = server.port + 1; // 通常查询端口是游戏端口+1
                    navigator.clipboard.writeText(`${server.node_host}:${queryPort}`);
                    message.success('查询端口已复制到剪贴板');
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = 'linear-gradient(135deg, #f6ffed 0%, #d9f7be 100%)';
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 8px 25px rgba(82, 196, 26, 0.3)';
                    e.currentTarget.style.borderColor = '#52c41a';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'linear-gradient(135deg, #f0f2f5 0%, #f6ffed 100%)';
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = 'none';
                    e.currentTarget.style.borderColor = 'transparent';
                  }}
                  >
                    {server.node_host}:{server.port + 1}
                  </div>
                </div>

                <Text type="secondary" style={{ fontSize: '13px', marginTop: '16px', display: 'block', opacity: 0.7 }}>
                  点击地址可复制到剪贴板
                </Text>
              </div>
            </Card>
          </Col>

          <Col xs={24} lg={12}>
            <Card
              style={{
                background: 'rgba(255, 255, 255, 0.98)',
                backdropFilter: 'blur(25px)',
                borderRadius: '20px',
                border: '1px solid rgba(255, 255, 255, 0.4)',
                boxShadow: '0 20px 60px rgba(0, 0, 0, 0.25)',
                height: '100%',
                transition: 'all 0.3s ease'
              }}
              bodyStyle={{ padding: '24px' }}
            >
              <div style={{ textAlign: 'center' }}>
                <CloudServerOutlined style={{ fontSize: '52px', color: '#52c41a', marginBottom: '20px' }} />
                <Title level={3} style={{ margin: '0 0 20px 0', fontSize: '20px', fontWeight: '600' }}>服务器信息</Title>
                <Row gutter={[16, 16]}>
                  <Col span={12}>
                    <div style={{ textAlign: 'center' }}>
                      <Statistic
                        title={
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                            <span>当前在线</span>
                            <Button
                              type="text"
                              size="small"
                              icon={<ReloadOutlined />}
                              loading={onlineCountLoading}
                              onClick={getOnlineCount}
                              style={{ 
                                padding: '0',
                                width: '20px',
                                height: '20px',
                                minWidth: '20px',
                                color: '#1890ff'
                              }}
                            />
                          </div>
                        }
                        value={onlineCountLoading ? '...' : (onlineCount !== null ? onlineCount : '--')}
                        prefix={<UserOutlined />}
                        valueStyle={{ 
                          color: onlineCount !== null && onlineCount > 0 ? '#52c41a' : '#999',
                          fontSize: '20px',
                          fontWeight: 'bold'
                        }}
                        suffix={onlineCount !== null ? `/${server.max_players}` : ''}
                      />
                    </div>
                  </Col>
                  <Col span={12}>
                    <Statistic
                      title="最大玩家数"
                      value={server.max_players}
                      prefix={<UserOutlined />}
                      valueStyle={{ color: '#1890ff' }}
                    />
                  </Col>
                  <Col span={12}>
                    <Statistic
                      title={
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                          <span>游戏模式</span>
                          <Button
                            type="text"
                            size="small"
                            icon={<ReloadOutlined />}
                            loading={serverInfoLoading}
                            onClick={getServerInfo}
                            style={{ 
                              padding: '0',
                              width: '20px',
                              height: '20px',
                              minWidth: '20px',
                              color: '#1890ff'
                            }}
                          />
                        </div>
                      }
                      value={serverInfoLoading ? '...' : (gameMode || '--')}
                      valueStyle={{ 
                        color: gameMode ? '#722ed1' : '#999',
                        fontSize: '16px',
                        fontWeight: 'bold'
                      }}
                    />
                  </Col>
                  <Col span={12}>
                    <Statistic
                      title="当前地图"
                      value={serverInfoLoading ? '...' : (mapName || '--')}
                      valueStyle={{ 
                        color: mapName ? '#fa8c16' : '#999',
                        fontSize: '16px',
                        fontWeight: 'bold'
                      }}
                    />
                  </Col>
                  <Col span={24}>
                    <Statistic
                      title="服务器节点"
                      value={server.node_name}
                      prefix={<DatabaseOutlined />}
                      valueStyle={{ color: '#13c2c2', fontSize: '14px' }}
                    />
                  </Col>
                </Row>
              </div>
            </Card>
          </Col>
        </Row>

        {/* 操作按钮 */}
        <div style={{
          textAlign: 'center',
          marginBottom: '40px',
          width: '100%'
        }}>
          <Space size="large" wrap style={{ width: '100%', justifyContent: 'center' }}>
            <Button
              type="primary"
              size="large"
              icon={<PlayCircleOutlined />}
              loading={joinLoading}
              onClick={handleJoinGame}
              disabled={server.status !== 'running' || isExpired}
              style={{
                height: '64px',
                minWidth: '220px',
                fontSize: '18px',
                fontWeight: 'bold',
                borderRadius: '16px',
                background: 'linear-gradient(135deg, #52c41a 0%, #73d13d 100%)',
                border: 'none',
                boxShadow: '0 12px 32px rgba(82, 196, 26, 0.4)',
                transition: 'all 0.3s ease',
                position: 'relative',
                overflow: 'hidden'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-3px)';
                e.currentTarget.style.boxShadow = '0 16px 40px rgba(82, 196, 26, 0.6)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 12px 32px rgba(82, 196, 26, 0.4)';
              }}
            >
              🎮 立即加入游戏
            </Button>

            <Button
              size="large"
              icon={<ShareAltOutlined />}
              loading={shareLoading}
              onClick={handleSharePage}
              style={{
                height: '64px',
                minWidth: '220px',
                fontSize: '18px',
                fontWeight: 'bold',
                borderRadius: '16px',
                background: 'linear-gradient(135deg, #13c2c2 0%, #36cfc9 100%)',
                border: 'none',
                color: 'white',
                boxShadow: '0 12px 32px rgba(19, 194, 194, 0.4)',
                transition: 'all 0.3s ease',
                position: 'relative',
                overflow: 'hidden'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-3px)';
                e.currentTarget.style.boxShadow = '0 16px 40px rgba(19, 194, 194, 0.6)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 12px 32px rgba(19, 194, 194, 0.4)';
              }}
            >
              📋 分享此页面
            </Button>

            {/* 背景切换按钮 */}
            {gameTypeBackground && (
              <Button
                size="large"
                icon={<ThunderboltOutlined />}
                loading={backgroundLoading}
                onClick={handleBackgroundSwitch}
                style={{
                  height: '64px',
                  minWidth: '180px',
                  fontSize: '18px',
                  fontWeight: 'bold',
                  borderRadius: '16px',
                  background: 'linear-gradient(135deg, #722ed1 0%, #9254de 100%)',
                  border: 'none',
                  color: 'white',
                  boxShadow: '0 12px 32px rgba(114, 46, 209, 0.4)',
                  transition: 'all 0.3s ease',
                  position: 'relative',
                  overflow: 'hidden'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-3px)';
                  e.currentTarget.style.boxShadow = '0 16px 40px rgba(114, 46, 209, 0.6)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 12px 32px rgba(114, 46, 209, 0.4)';
                }}
              >
                🎨 {currentBackground === 'game' ? '切换默认背景' : '切换游戏背景'}
              </Button>
            )}
          </Space>
        </div>

        {/* 状态提示 */}
        {server.status !== 'running' && (
          <div style={{
            textAlign: 'center',
            marginBottom: '40px',
            width: '100%'
          }}>
            <Card
              style={{
                background: 'rgba(255, 255, 255, 0.95)',
                backdropFilter: 'blur(20px)',
                borderRadius: '16px',
                border: '1px solid rgba(255, 255, 255, 0.3)',
                boxShadow: '0 16px 48px rgba(0, 0, 0, 0.2)',
                maxWidth: '600px',
                margin: '0 auto'
              }}
            >
              <div style={{ padding: '20px' }}>
                <ClockCircleOutlined style={{ fontSize: '48px', color: '#faad14', marginBottom: '16px' }} />
                <Title level={4} style={{ margin: '0 0 8px 0' }}>
                  {server.status === 'stopped' ? '服务器已停止' : 
                   server.status === 'starting' ? '服务器启动中' : 
                   server.status === 'restarting' ? '服务器重启中' : '服务器状态异常'}
                </Title>
                <Paragraph style={{ margin: 0, color: '#666' }}>
                  {server.status === 'stopped' ? '服务器当前处于停止状态，请联系管理员启动服务器' :
                   server.status === 'starting' ? '服务器正在启动中，请稍后再试' :
                   server.status === 'restarting' ? '服务器正在重启中，请稍后再试' : '服务器状态异常，请联系管理员'}
                </Paragraph>
              </div>
            </Card>
          </div>
        )}

        {isExpired && (
          <div style={{
            textAlign: 'center',
            marginBottom: '40px',
            width: '100%'
          }}>
            <Card
              style={{
                background: 'rgba(255, 255, 255, 0.95)',
                backdropFilter: 'blur(20px)',
                borderRadius: '16px',
                border: '1px solid rgba(255, 255, 255, 0.3)',
                boxShadow: '0 16px 48px rgba(0, 0, 0, 0.2)',
                maxWidth: '600px',
                margin: '0 auto'
              }}
            >
              <div style={{ padding: '20px' }}>
                <RocketOutlined style={{ fontSize: '48px', color: '#ff4d4f', marginBottom: '16px' }} />
                <Title level={4} style={{ margin: '0 0 8px 0', color: '#ff4d4f' }}>
                  服务器已过期
                </Title>
                <Paragraph style={{ margin: 0, color: '#666' }}>
                  该服务器已过期，请联系管理员续费
                </Paragraph>
              </div>
            </Card>
          </div>
        )}

      </div>

      {/* CSS动画 */}
      <style>{`
        @keyframes float {
          0%, 100% {
            transform: translateY(0px) rotate(0deg);
            opacity: 0.6;
          }
          50% {
            transform: translateY(-20px) rotate(180deg);
            opacity: 1;
          }
        }
        
        @keyframes pulse {
          0% {
            box-shadow: 0 8px 24px rgba(82, 196, 26, 0.4);
          }
          50% {
            box-shadow: 0 8px 24px rgba(82, 196, 26, 0.8);
          }
          100% {
            box-shadow: 0 8px 24px rgba(82, 196, 26, 0.4);
          }
        }
        
        .join-button:hover {
          animation: pulse 1.5s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
};

export default ServerShare;
